import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jobApi } from '../../../api/job';
import { testsuiteApi } from '../../../api/testsuite';
import { testDataApi } from '../../../api/testdata';
import { clearAuthData } from '../../../utils/auth';
import Navbar from '../../layout/Navbar';
import DataFixture from '../../sections/DataFixture';
import ScheduleTestModal from '../../modals/ScheduleTestModal';

function TestSuiteList() {
  const [testSuites, setTestSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [selectedSuites, setSelectedSuites] = useState({});
  const [batchDeleting, setBatchDeleting] = useState(false);
  const navigate = useNavigate();
  
  // Calculate selected count once 
  const selectedCount = Object.values(selectedSuites).filter(Boolean).length;
  
  // Fixture selection state
  const [isFixtureModalOpen, setIsFixtureModalOpen] = useState(false);
  const [selectedSuiteId, setSelectedSuiteId] = useState(null);

  // Schedule modal state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedSuiteForSchedule, setSelectedSuiteForSchedule] = useState(null);

  useEffect(() => {
    fetchTestSuites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const fetchTestSuites = async () => {
    try {
      setLoading(true);
      const response = await testsuiteApi.getTestSuites(sortBy);
      setTestSuites(response.test_suites || []);
      
      if (response.sort_settings?.current_sort) {
        setSortBy(response.sort_settings.current_sort);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching test suites:', error);
      setError('Failed to load test suites, ' + error.message);
      setLoading(false);
      
      // If session expired, clear all auth data and redirect to login
      if (error.message.includes('session has expired')) {
        clearAuthData();
        navigate('/login');
      }
    }
  };

  const openFixtureModal = async (suiteId) => {
    setSelectedSuiteId(suiteId);
    setIsFixtureModalOpen(true);
  };

  const closeFixtureModal = () => {
    setIsFixtureModalOpen(false);
    setSelectedSuiteId(null);
  };

  const handleFixtureSelect = async (fixture) => {
    if (!selectedSuiteId) return;

    try {
      const data = await testsuiteApi.runTestSuiteWithFixture(selectedSuiteId, fixture.id);
      closeFixtureModal();
      
      if (data.id) {
        navigate(`/job/${data.id}`);
      } else {
        throw new Error('No job ID returned from test suite run');
      }
    } catch (error) {
      console.error('Error starting test suite run:', error);
      setError('Failed to start test suite run');
      
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const handleRunWithoutFixture = async () => {
    if (!selectedSuiteId) return;

    try {
      const data = await testsuiteApi.runTestSuite(selectedSuiteId);
      closeFixtureModal();
      
      if (data.id) {
        navigate(`/job/${data.id}`);
      } else {
        throw new Error('No job ID returned from test suite run');
      }
    } catch (error) {
      console.error('Error starting test suite run:', error);
      setError('Failed to start test suite run');
      
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const handleRunTestSuite = async (suiteId) => {
    // Open fixture modal instead of running directly
    await openFixtureModal(suiteId);
  };

  const handleScheduleTestSuite = (suite) => {
    setSelectedSuiteForSchedule(suite);
    setIsScheduleModalOpen(true);
  };

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setSelectedSuiteForSchedule(null);
  };

  const handleDeleteTestSuite = async (suiteId, suiteName) => {
    if (!window.confirm(`Are you sure you want to delete the test suite "${suiteName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await testsuiteApi.deleteTestSuite(suiteId);
      
      setError(null);
      setSuccessMessage(`Successfully deleted test suite "${suiteName}"`);
      
      fetchTestSuites();
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Error deleting test suite:', error);
      setError(`Failed to delete test suite: ${error.message}`);
      
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const toggleSelectAll = () => {
    const allSelected = testSuites.every(suite => selectedSuites[suite.id]);
    
    if (allSelected) {
      // Deselect all
      setSelectedSuites({});
    } else {
      // Select all
      const newSelected = {};
      testSuites.forEach(suite => {
        newSelected[suite.id] = true;
      });
      setSelectedSuites(newSelected);
    }
  };

  const toggleSelectTestSuite = (suiteId) => {
    // Simple toggle of the selection state
    setSelectedSuites(prev => ({
      ...prev,
      [suiteId]: !prev[suiteId]
    }));
  };

  const handleBatchDelete = async () => {
    const selectedIds = Object.keys(selectedSuites).filter(id => selectedSuites[id]);
    
    if (selectedIds.length === 0) {
      setError("No test suites selected");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} test suite${selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.`)) {
      return;
    }

    try {
      setBatchDeleting(true);
      await testsuiteApi.batchDeleteTestSuites(selectedIds);
      
      setError(null);
      setSuccessMessage(`Successfully deleted ${selectedIds.length} test suite${selectedIds.length > 1 ? 's' : ''}`);
      
      // Clear selections
      setSelectedSuites({});
      
      // Refresh the list
      fetchTestSuites();
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Error batch deleting test suites:', error);
      setError(`Failed to delete test suites: ${error.message}`);
      
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setBatchDeleting(false);
    }
  };

  const handleExportSuitesJson = () => {
    const data = testSuites.map(suite => ({
      suite_name: suite.suite_name,
      description: suite.description || '',
      created_at: suite.created_at
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'test suites list.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Test Suites</h1>
          <div className="flex flex-wrap gap-2">
            {selectedCount > 0 && (
              <button
                onClick={handleBatchDelete}
                disabled={batchDeleting}
                className={`${
                  batchDeleting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                } text-white font-bold py-2 px-4 rounded transition-colors flex items-center gap-1`}
              >
                {batchDeleting ? (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
                {batchDeleting ? "Deleting..." : "Delete Selected"}
                {selectedCount > 0 && !batchDeleting && (
                  <span className="ml-1 bg-white text-red-600 rounded-full px-2 py-0.5 text-xs font-bold">
                    {selectedCount}
                  </span>
                )}
              </button>
            )}
            
            <button
              onClick={handleExportSuitesJson}
              disabled={testSuites.length === 0}
              className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export JSON
            </button>
            <Link
              to="/testsuites/create"
              className="bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Create New Test Suite
            </Link>
          </div>
        </div>

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
            {successMessage}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading test suites...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        ) : testSuites.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No test suites found. Create your first test suite!</p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex justify-end">
              <select
                value={sortBy || 'updated_desc'}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              >
                <option value="updated_desc">Most Recently Updated</option>
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
              </select>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              {/* Desktop: table layout (xl and up to avoid button clipping at intermediate widths) */}
              <div className="hidden xl:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                          checked={testSuites.length > 0 && testSuites.every(suite => selectedSuites[suite.id])}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelectAll();
                          }}
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {testSuites.map((suite) => (
                      <tr 
                        key={suite.id} 
                        className={`hover:bg-gray-50 ${selectedSuites[suite.id] ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                            checked={!!selectedSuites[suite.id]}
                            onChange={() => toggleSelectTestSuite(suite.id)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {suite.suite_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(suite.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleRunTestSuite(suite.id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Run Suite
                            </button>

                            <button
                              onClick={() => handleScheduleTestSuite(suite)}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Schedule
                            </button>
                            
                            <button
                              onClick={() => navigate(`/testsuites/${suite.id}/tests`)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Tests
                            </button>

                            <button
                              onClick={() => navigate(`/testsuites/${suite.id}/workflows`)}
                              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Workflows
                            </button>
                            
                            <button
                              onClick={() => navigate(`/testsuites/${suite.id}/edit`)}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            
                            <button
                              onClick={() => handleDeleteTestSuite(suite.id, suite.suite_name)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Narrow/intermediate screens: card layout with all buttons visible and wrapping */}
              <div className="xl:hidden divide-y divide-gray-200">
                {testSuites.map((suite) => (
                  <div
                    key={suite.id}
                    className={`px-4 py-4 hover:bg-gray-50 ${selectedSuites[suite.id] ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5 flex-shrink-0"
                          checked={!!selectedSuites[suite.id]}
                          onChange={() => toggleSelectTestSuite(suite.id)}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {suite.suite_name}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5">
                            {formatDate(suite.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleRunTestSuite(suite.id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Run Suite
                        </button>
                        <button
                          onClick={() => handleScheduleTestSuite(suite)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Schedule
                        </button>
                        <button
                          onClick={() => navigate(`/testsuites/${suite.id}/tests`)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Tests
                        </button>
                        <button
                          onClick={() => navigate(`/testsuites/${suite.id}/workflows`)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Workflows
                        </button>
                        <button
                          onClick={() => navigate(`/testsuites/${suite.id}/edit`)}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTestSuite(suite.id, suite.suite_name)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <DataFixture 
        isOpen={isFixtureModalOpen}
        onClose={closeFixtureModal}
        onSelect={handleFixtureSelect}
        onRunWithoutFixture={handleRunWithoutFixture}
        title="Select Test Data Fixture"
        description="Select a data fixture to use for this test suite run or run without a fixture."
      />

      <ScheduleTestModal
        isOpen={isScheduleModalOpen}
        onClose={closeScheduleModal}
        suiteId={selectedSuiteForSchedule?.id}
        suiteName={selectedSuiteForSchedule?.suite_name}
      />
    </div>
  );
}

export default TestSuiteList; 